import { useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOSchema from "@/components/base/SEOSchema";
import { reviews } from "@/mocks/articles";

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== "") {
      setFormStatus("success");
      form.reset();
      setTimeout(() => setFormStatus("idle"), 3000);
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch("https://readdy.ai/api/form/d9njpm68mbnljin5a220", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === "OK") {
        setFormStatus("success");
        form.reset();
        setTimeout(() => setFormStatus("idle"), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || "提交失败，请稍后重试");
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查网络连接后重试");
      setFormStatus("error");
    }
  };

  return (
    <>
      <SEOSchema
        type="WebPage"
        data={{
          name: "关于我们 - 91影视看片下载",
          description: "91影视看片下载官网品牌介绍，了解我们的品牌故事、用户口碑和联系方式。",
          url: "https://www.91yingshi.com/about",
          isPartOf: {
            "@type": "WebSite",
            name: "91影视看片下载",
            url: "https://www.91yingshi.com",
          },
        }}
      />
      <SEOSchema
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.91yingshi.com",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "关于我们",
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <Breadcrumb items={[{ label: t("about_title") }]} />

          {/* Page Header */}
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                {t("about_title")}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                91影视看片致力于为安卓用户提供最优质的免费影视观看体验，海量资源一站畅享
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full py-14 md:py-18">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                    {t("about_brand")}
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <p>
                      91影视看片诞生于2019年，由一群热爱影视的技术极客创立。我们深知用户在寻找免费、高质量影视资源时的痛点——要么资源不全，要么广告满天飞，要么需要付费会员。
                    </p>
                    <p>
                      因此，我们打造了91影视看片APP——一款真正免费的手机影视软件。经过多年的迭代优化，91影视看片已发展成为拥有超过5000万用户的影视平台，覆盖电影、电视剧、综艺、动漫、纪录片等全品类内容。
                    </p>
                    <p>
                      我们的使命是让每个人都能随时随地享受高清影视的乐趣，无需为观看内容支付任何费用。我们相信，好的内容应该被更多人看到。
                    </p>
                  </div>
                </div>
                <div className="relative h-72 md:h-80 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20tech%20office%20workspace%20with%20developers%20collaborating%2C%20warm%20lighting%2C%20minimalist%20design%2C%20creative%20atmosphere%2C%20clean%20aesthetic%2C%20professional%20environment%2C%20soft%20natural%20light&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="91影视看片品牌故事"
                    className="w-full h-full object-cover object-top rounded-lg"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="w-full py-14 md:py-18 bg-background-100">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
                我们的核心价值
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: "ri-heart-line", title: "用户至上", desc: "一切以用户体验为核心，持续优化产品功能和播放体验" },
                  { icon: "ri-shield-star-line", title: "品质保证", desc: "精选优质影视资源，提供高清画质和稳定流畅的播放服务" },
                  { icon: "ri-lightbulb-line", title: "技术创新", desc: "采用P2P加速等前沿技术，不断提升播放速度和资源丰富度" },
                  { icon: "ri-group-line", title: "开放共赢", desc: "与内容提供方合作共赢，构建健康的影视生态" },
                ].map((v, i) => (
                  <div key={i} className="text-center">
                    <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-4">
                      <i className={`${v.icon} text-2xl text-primary-500`}></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* User Reviews */}
          <section className="w-full py-14 md:py-18">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
                {t("about_reputation")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {reviews.map((review, i) => (
                  <div key={i} className="bg-background-100 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{review.name}</div>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: review.rating }).map((_, j) => (
                            <i key={j} className="ri-star-fill text-xs text-accent-500"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                    <div className="mt-3 text-xs text-foreground-400">{review.date}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="w-full py-14 md:py-18 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">
                {t("about_contact")}
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                有任何问题或建议，欢迎给我们留言，我们会尽快回复
              </p>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 rounded-lg p-6 md:p-8 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-2">
                      {t("contact_name")}
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-2">
                      {t("contact_email")}
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-2">
                    {t("contact_message")}
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>

                <div className="honeypot-field" aria-hidden="true">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10"
                  />
                </div>

                <div>
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer"
                  >
                    <i className="ri-send-plane-line"></i>
                    {t("contact_submit")}
                  </button>
                </div>

                {formStatus === "success" && (
                  <div className="p-4 bg-accent-100 text-accent-800 rounded-md text-sm">
                    <i className="ri-check-line mr-1"></i>
                    {t("contact_success")}
                  </div>
                )}
                {formStatus === "error" && (
                  <div className="p-4 bg-red-50 text-red-700 rounded-md text-sm">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </section>

          {/* Contact Info Bar */}
          <section className="w-full py-10 bg-foreground-900">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500/20 rounded-full mx-auto mb-3">
                    <i className="ri-mail-line text-primary-400"></i>
                  </div>
                  <div className="text-sm text-background-50/60 mb-1">电子邮箱</div>
                  <div className="text-sm text-background-50/90">support@91yingshi.com</div>
                </div>
                <div>
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500/20 rounded-full mx-auto mb-3">
                    <i className="ri-wechat-line text-primary-400"></i>
                  </div>
                  <div className="text-sm text-background-50/60 mb-1">客服微信</div>
                  <div className="text-sm text-background-50/90">91yingshi_kf</div>
                </div>
                <div>
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500/20 rounded-full mx-auto mb-3">
                    <i className="ri-time-line text-primary-400"></i>
                  </div>
                  <div className="text-sm text-background-50/60 mb-1">工作时间</div>
                  <div className="text-sm text-background-50/90">每天 9:00 - 21:00</div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}